import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  CaseRecommendationState,
  RecommendedCases,
  StrategyContext,
  StrategyType,
  AllocationDpdFilter
} from '../types';

export interface ExtendedCaseRecommendationState extends CaseRecommendationState {
  strategyContext: StrategyContext | null;
  isEndOfNavigation: boolean;
  showSelectStrategyPrompt: boolean;
  showEndOfNavigationModal: boolean;
  showViewingStrategyBanner: boolean;
  showChangeStrategyConfirmModal: boolean;
  pendingStrategyChange: {
    allocationDpdFilter: AllocationDpdFilter;
    strategyType: StrategyType;
  } | null;
}

const initialState: ExtendedCaseRecommendationState = {
  isModalOpen: false,
  onStartCallingCallback: undefined,
  recommendedCases: {
    lastRefreshedInMs: 0,
    caseList: []
  },
  currentRecommendedCase: {
    customerReferenceId: '',
    customerName: '',
    entityId: '',
    caseReferenceId: '',
    businessVertical: '',
    priority: 0,
    totalCases: 0,
    attributes: {
      customerName: '',
      latestDisposition: '',
      ptpState: '',
      isContactable: false,
      latestInteractionTimestamp: 0
    }
  },
  recommendedMetadata: {
    totalCases: 0,
    visitedCases: 0,
    pendingPtps: 0,
    lastRefreshedInMs: 0
  },
  isLoadingRecommendedCases: false,
  strategyContext: null,
  isEndOfNavigation: false,
  showSelectStrategyPrompt: false,
  showEndOfNavigationModal: false,
  showViewingStrategyBanner: false,
  showChangeStrategyConfirmModal: false,
  pendingStrategyChange: null
};

const CaseRecommendationSlice = createSlice({
  name: 'caseRecommendation',
  initialState,
  reducers: {
    setRecommendedMetadata: (state, action) => {
      state.recommendedMetadata = action.payload;
    },
    setRecommendedCases: (state, action) => {
      state.recommendedCases = action.payload;
    },
    appendRecommendedCases: (state, action: PayloadAction<RecommendedCases>) => {
      state.recommendedCases.caseList = [
        ...state.recommendedCases.caseList,
        ...action.payload.caseList
      ];
      state.recommendedCases.lastRefreshedInMs = action.payload.lastRefreshedInMs;
    },
    setRecommendedCasesLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoadingRecommendedCases = action.payload;
    },
    setCurrentRecommendedCase: (state, action) => {
      state.currentRecommendedCase = action.payload;
    },
    openRecommendedModal: state => {
      state.isModalOpen = true;
    },
    closeRecommendedModal: state => {
      state.isModalOpen = false;
      state.onStartCallingCallback = undefined;
    },
    setRecommendedModalCallback: (state, action: PayloadAction<string>) => {
      state.onStartCallingCallback = action.payload;
    },
    setStrategyContext: (state, action: PayloadAction<StrategyContext | null>) => {
      state.strategyContext = action.payload;
      state.showSelectStrategyPrompt = false;
    },
    updateStrategyPosition: (
      state,
      action: PayloadAction<{ currentPosition: number; isEndOfNavigation: boolean }>
    ) => {
      if (state.strategyContext) {
        state.strategyContext.currentPositionInStrategy = action.payload.currentPosition;
      }
      state.isEndOfNavigation = action.payload.isEndOfNavigation;
      if (action.payload.isEndOfNavigation) {
        state.showEndOfNavigationModal = true;
      }
    },
    setShowSelectStrategyPrompt: (state, action: PayloadAction<boolean>) => {
      state.showSelectStrategyPrompt = action.payload;
    },
    setShowEndOfNavigationModal: (state, action: PayloadAction<boolean>) => {
      state.showEndOfNavigationModal = action.payload;
    },
    clearStrategyContext: state => {
      state.strategyContext = null;
      state.isEndOfNavigation = false;
      state.showSelectStrategyPrompt = false;
      state.showEndOfNavigationModal = false;
      state.showViewingStrategyBanner = false;
    },
    setShowViewingStrategyBanner: (state, action: PayloadAction<boolean>) => {
      state.showViewingStrategyBanner = action.payload;
    },
    setShowChangeStrategyConfirmModal: (state, action: PayloadAction<boolean>) => {
      state.showChangeStrategyConfirmModal = action.payload;
      if (!action.payload) {
        state.pendingStrategyChange = null;
      }
    },
    setPendingStrategyChange: (
      state,
      action: PayloadAction<{
        allocationDpdFilter: AllocationDpdFilter;
        strategyType: StrategyType;
      } | null>
    ) => {
      state.pendingStrategyChange = action.payload;
      if (action.payload) {
        state.showChangeStrategyConfirmModal = true;
      }
    },
    setStrategyContextWithBanner: (state, action: PayloadAction<StrategyContext | null>) => {
      state.strategyContext = action.payload;
      state.showSelectStrategyPrompt = false;
      state.showViewingStrategyBanner = action.payload !== null;
    }
  }
});

export const {
  openRecommendedModal,
  closeRecommendedModal,
  setRecommendedModalCallback,
  setRecommendedCases,
  appendRecommendedCases,
  setRecommendedCasesLoading,
  setCurrentRecommendedCase,
  setRecommendedMetadata,
  setStrategyContext,
  updateStrategyPosition,
  setShowSelectStrategyPrompt,
  setShowEndOfNavigationModal,
  clearStrategyContext,
  setShowViewingStrategyBanner,
  setShowChangeStrategyConfirmModal,
  setPendingStrategyChange,
  setStrategyContextWithBanner
} = CaseRecommendationSlice.actions;

export default CaseRecommendationSlice.reducer;
